<script>
import Sortable from 'sortablejs';

/**
 * 动态表格，可根据传入的columns设置进行表格列内容的渲染
 */
export default {
  name: 'DataTable',
  props: {
    columns: Array,
    // 查询结果数据
    tableData: {
      type: Array,
      default() {
        return [];
      }
    },
    tableHeight: null,
    rowStyle: {
      type: Object,
      default() {
        return {};
      }
    },
    // 是否显示pager组件
    isShowPager: {
      type: Boolean,
      default: true
    },
    // 是否显示操作里的
    showTooltip: {
      type: Boolean,
      default: false
    },
    //该属性仅对type="selection" 的列有效，当设置为true时，数据更新后会保留之前选中的数据，但需指定row-key属性才能生效：
    reserveSelection: {
      type: Boolean,
      default: true
    },
    // 查询结果总数量
    total: null,
    // 当前显示页号
    pageNum: {
      type: Number,
      default: 1
    },
    // 当前每页显示数据数量
    pageSize: {
      type: Number,
      default: 10
    },
    rowKeyName: {
      type: String,
      default: "id"
    },
    dragClassName: {
      type: String,
      default: "drag-class"
    },
  },
  watch: {
    tableData: {
      handler(newVal, oldVal) {
        this._tableData = [...newVal];
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      },
      deep: true,
    },
  },
  data() {
    return {
      pageSizes: [10, 20, 50, 100],
      dragable: false,
      _tableData: [],
      originalData: [],
    };
  },
  created() {
    this._tableData = [...this.tableData];
    let targetInstance = window;
    if (window.top !== targetInstance) {
      targetInstance = window.top;
    }
    if (targetInstance.dynamicTablePageSizes && typeof targetInstance.dynamicTablePageSizes === 'string') {
      this.pageSizes = targetInstance.dynamicTablePageSizes.split(',');
    }
  },
  mounted() {
    if (this.dragable) {
      this.initDrag();
    }
  },
  methods: {
    /**
     * 当前页号变更事件处理
     * @event
     * @param {Number} val 变更后的页号
     */
    onCurrentChange(val) {
      this.$emit('current-change', val);
    },
    /**
     * 当前表格数据显示数量变更事件处理
     * @event
     * @param {Number} val 变更后的表格数据显示数量
     */
    onSizeChange(val) {
      this.$emit('size-change', val);
    },
    /**
     * 选择项发生变化时事件处理
     * @event
     * @param {Array} val 当前的选择项
     */
    onSelectionChange(val) {
      this.$emit('selection-change', val);
    },
    /**
     * 行数据的 Key，用来优化 Table 的渲染（多选翻页不取消，如使用reserve-selection，则row-key必填）
     * @event
     * @param {Array} val 当前的选择项
     */
    onRowKey(val) {
      return val[this.rowKeyName]
    },
    /**
     * 设定复选框的选中状态
     * @event
     * @param {Object} row 行数据
     * @param {Boolean} selected 事件对象
     */
    toggleRowSelection(row, selected) {
      this.$refs.ElTable.toggleRowSelection(row, selected);
    },

    /**
     * 渲染表格Column
     * @param {Object} props Column属性
     */
    renderColumn(props) {
      let ret = null;
      if (props.type && props.type == 'drag') {
        this.dragable = true;
        const scopedSlots =
          typeof props.render === 'function'
            ? {
              default: scope => props.render(scope)
            }
            : null;
        ret = (
          <ElTableColumn
            label={props.title}
            prop={props.key}
            formatter={props.formatter}
            width={props.width || 55}
            align="center"
            scopedSlots={scopedSlots}
            reserve-selection={props.reserveSelection}
            show-overflow-tooltip={props.showTooltip}
          ></ElTableColumn>
        );
      } else if (props.type && props.type !== '') {
        ret = (
          <ElTableColumn
            label={props.title}
            type={props.type}
            width={props.width}
            index={props.index}
            align="center"
            selectable={props.selectable}
            reserve-selection={props.reserveSelection}
          ></ElTableColumn>
        );
      } else {
        const scopedSlots =
          typeof props.render === 'function'
            ? {
              default: scope => props.render(scope)
            }
            : null;
        ret = (
          <ElTableColumn
            label={props.title}
            prop={props.key}
            formatter={props.formatter}
            width={props.width}
            align="center"
            scopedSlots={scopedSlots}
            reserve-selection={props.reserveSelection}
            show-overflow-tooltip={props.showTooltip}
          ></ElTableColumn>
        );
      }
      return ret;
    },

    /**
      * 拖拽相关
      */

    initDrag() {
      // 获取表格的tbody元素
      const tableBody = this.$refs.ElTable.$el.querySelector('.el-table__body-wrapper tbody');
      // 初始化SortableJS拖拽
      const sortable = new Sortable(tableBody, {
        handle: `.${this.dragClassName}`, // 拖拽的控制元素
        onStart: this.onDragStart, // 拖拽开始时
        onEnd: this.onDragEnd, // 拖拽结束时
        onMove: this.onDragMove, // 拖拽过程中实时更新
      });
    },
    onDragStart(event) {
      // 拖拽开始时保存原始位置
      this.originalData = [...this._tableData];
    },
    onDragMove(evt) {
      // 阻止冒泡
      evt.stopPropagation();
      // 我们不在这里更新数据，而是返回true允许放置
      return true;
    },

    onDragEnd(evt) {
      // 拖拽结束时更新表格数据
      const newData = [...this._tableData];
      const draggedItem = newData.splice(evt.oldIndex, 1)[0];
      newData.splice(evt.newIndex, 0, draggedItem);
      // 实时更新数据
      this._tableData = newData;
      // 如果需要，可以在这里进行额外的操作，如保存排序到后台

      this.$emit('onDragEnd', this._tableData)
    },

    getTableData() {
      return this._tableData;
    },

    setTableData(tableData) {
      this._tableData = [...tableData]
      this.$forceUpdate();
    },
  },
  /**
   * Vue实例渲染函数，生成组件模版
   */
  render() {
    const {
      columns,
      pageNum,
      pageSize,
      pageSizes,
      onCurrentChange,
      onSelectionChange,
      onSizeChange,
      onRowKey = {},
      renderColumn,
      tableData = [],
      rowStyle = {},
      tableHeight,
      total,
      isShowPager,
      $slots,
      dragable,
      _tableData = [],
    } = this;
    const renderColumns = [];
    columns.forEach(column => {
      const { children } = column;
      // Column的场合
      if (children === null || children === undefined) {
        renderColumns.push(renderColumn(column));
        // ColumnGroup的场合
      } else {
        const childColumnList = children.map(childColumn => renderColumn(childColumn));
        renderColumns.push(
          <ElTableColumnGroup name={column.key} label={column.title}>
            {childColumnList}
          </ElTableColumnGroup>
        );
      }
    });
    let tableLength = _tableData.length
    return (
      <div class="table">
        <ElTable
          ref="ElTable"
          data={_tableData}
          border
          height={tableHeight}
          row-style={rowStyle}
          header-cell-class-name="table-header"
          on-selection-change={onSelectionChange}
          row-key={onRowKey}
          stripe
          isShowPager={isShowPager}
        >
          {$slots['batch-control'] ? <template slot="control">{$slots['batch-control']}</template> : null}
          {renderColumns}
        </ElTable>
        {tableLength > 0 && isShowPager ? (
          <div class="pagination">
            <ElPagination
              total={total}
              current-page={pageNum}
              page-size={pageSize}
              page-sizes={pageSizes}
              on-current-change={onCurrentChange}
              on-size-change={onSizeChange}
            ></ElPagination>
          </div>
        ) : null}
      </div>
    );
  }
};
</script>
<style lang="scss">
.handle-box {
  margin-bottom: 20px;
}

.handle-select {
  width: 120px;
}

.handle-input {
  width: 300px;
  display: inline-block;
}

.table {
  width: 100%;
  font-size: 14px;
}

.mr10 {
  margin-right: 10px;
}

.table-td-thumb {
  display: block;
  margin: auto;
  width: 40px;
  height: 40px;
}

.el-icon-rank {
  cursor: move;
  font-size: 20px;
  color: #409EFF;
}
</style>